/// <amd-module name="TFS/Admin/Views/TeamSelector/TeamPickerProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";

import { IArtifactPickerProviderOptions, ArtifactPickerProviderBase } from "Favorites/Picker/ArtifactPickerProvider";
import { Favorite } from "Favorites/Common/Contracts/Favorite";

import { IPickListAction } from "VSSUI/Components/PickList";
import { IPickListGroup } from "VSSUI/Components/PickList/PickList.Props";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";

import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { WebApiTeam, WebApiTeamRef, CategorizedWebApiTeams } from "Tfs/Platform/Contracts/Core";

import * as Resources from "TFS/Admin/Views/TeamSelector/Resources";

export interface ITeamPickerItem {
    /**
     * Name of Team to display in picker
     */
    name: string;

    /**
     * Id of Team to display in picker
     */
    id: string;

    /**
     * Dscription of team
     */
    description?: string;

    /**
     * Name of project team belongs to.
     */
    projectName?: string;

    /**
     * Id of project team belongs to.
     */
    projectId?: string;

    /**
     * Group in picker this team belongs to.
     */
    groupKey?: string;

    /**
     * Url of image for the team.
     */
    identityUrl?: string;
}

export interface ITeamPickerOptions {
    /**
     * The name of the initially selected team
     */
    initialTeam?: WebApiTeamRef | null;

    /**
     * Optional delegate to get the href property of the pick list item
     */
    getItemHref?: (teamItem: ITeamPickerItem) => string;

    /**
     * Optional list of actions to render after the repositories in the list
     */
    actions?: IPickListAction[];

    /**
     * Callback invoked whenever a new project is selected
     */
    onTeamSelected?: (selectedTeam: ITeamPickerItem) => void | boolean;

    /**
     * Header text for the default group
     */
    defaultGroupHeader?: string;

    /**
     * Header text for the favorites group
     */
    favoriteGroupHeader?: string;

    /**
     * Provides a way to hide the all teams group (and not even make this call).
     */
    hideAllTeams?: boolean;

    /**
     * Project to restrict the results to
     */
    projectId: string;

    /**
     * Optional boolean to control searchability
     */
    isSearchable?: boolean;
}

export class TeamPickerProvider extends ArtifactPickerProviderBase<ITeamPickerItem> {
    private itemsPromise: Promise<ITeamPickerItem[]>;
    private itemsGroup: IPickListGroup;
    private hideAllTeams: boolean | undefined;
    private pageContext: IVssPageContext;
    private projectId: string;

    constructor(pageContext: IVssPageContext, options: ITeamPickerOptions) {
        super();

        this.pageContext = pageContext;
        this.projectId = options.projectId;

        this.hideAllTeams = options.hideAllTeams;
        this.itemsGroup = {
            isLoading: true,
            key: "MoreTeams",
            name: Resources.MoreTeams
        };

        const { actions, getItemHref, onTeamSelected, defaultGroupHeader, favoriteGroupHeader, initialTeam } = options;

        const favoritesContext = {
            artifactType: "Microsoft.TeamFoundation.Teams.Team",
            artifactScope: {
                id: this.projectId,
                name: "",
                type: "Project"
            }
        };

        let selectedItem: ITeamPickerItem | undefined;
        if (initialTeam) {
            selectedItem = {
                name: initialTeam.name,
                id: initialTeam.id
            };
        } else if (initialTeam === undefined) {
            const teamService = this.pageContext.getService<ITfsTeamService>("ITfsTeamService");
            const pageTeam = teamService.getPageTeam();
            if (pageTeam) {
                selectedItem = {
                    name: pageTeam.name,
                    id: pageTeam.id,
                    projectId: pageTeam.projectId,
                    projectName: pageTeam.projectName
                };
            }
        }

        const artifactPickerOptions: IArtifactPickerProviderOptions<ITeamPickerItem> = {
            pageContext: pageContext,
            favoritesContext: favoritesContext,
            selectedArtifact: selectedItem,
            onArtifactClicked: onTeamSelected,
            hideFavoriteSelectedItemIndicator: true,
            getRootItemText: team => {
                return team.name;
            },
            getArtifacts: this.getPickListItems,
            getArtifactId: team => team.id,
            getArtifactName: team => team.name + " (" + team.projectName + ")",
            getArtifactHref: getItemHref,
            getArtifactFromFavorite: favorite => {
                return {
                    name: favorite.artifactName,
                    id: favorite.artifactId,
                    projectName: favorite.artifactScope.name,
                    projectId: favorite.artifactScope.id,
                    groupKey: "",
                    identityUrl: "",
                    description: ""
                };
            },
            getFavoriteFromArtifact: team => {
                return {
                    artifactId: team.id,
                    artifactName: team.name,
                    artifactScope: {
                        id: team.projectId,
                        name: team.projectName,
                        type: "Project"
                    },
                    artifactType: favoritesContext!.artifactType
                } as Favorite;
            },
            getArtifactIcon: (team: ITeamPickerItem): IVssIconProps => {
                return {
                    iconType: VssIconType.image,
                    imageProps: {
                        src: team.identityUrl,
                        className: "team-icon",
                        imageFit: 2 /*ImageFit.cover*/
                    }
                };
            },
            getArtifactListGroupId: (team: ITeamPickerItem): string => {
                return team.groupKey || "";
            },
            artifactComparer: (team1: ITeamPickerItem, team2: ITeamPickerItem): number => {
                return team1.name.localeCompare(team2.name, navigator.language);
            },
            defaultGroupHeader,
            favoriteGroupHeader,
            otherActions: actions,
            isSearchable: options.isSearchable,
            searchTextPlaceholder: Resources.FilterTeams,
            searchNoResultsText: Resources.NoTeamSearchResults,
            groups: [this.itemsGroup]
        };

        this.initialize(artifactPickerOptions);
    }

    private getPickListItems = (): Promise<ITeamPickerItem[]> => {
        if (!this.itemsPromise) {
            const teamService = this.pageContext.getService<ITfsTeamService>("ITfsTeamService");
            const teamsPromise = this.hideAllTeams ? Promise.resolve(null) : teamService.getTeamsCategorized(this.projectId);

            this.itemsPromise = teamsPromise.then(response => {
                const teamItems = this.getTeamItemsFromResponse(response);

                teamItems.sort((team1, team2) => localeIgnoreCaseComparer(team1.name, team2.name));

                this.itemsGroup.isLoading = false;
                return teamItems;
            });
        }

        return this.itemsPromise;
    }

    private getTeamItemsFromResponse(response: CategorizedWebApiTeams | null): ITeamPickerItem[] {
        const teamItems: ITeamPickerItem[] = [];

        response?.myTeams.forEach(team => {
            const identiyImageUrl = routeUrl(this.pageContext, "ms.vss-tfs-web.identity-image-route", { size: "0", id: team.id });
            const pickerItem = this.mapApiTeamToPickerItem(team, identiyImageUrl, "");
            teamItems.push(pickerItem);
        });

        response?.otherReadableTeams.forEach(team => {
            const identiyImageUrl = routeUrl(this.pageContext, "ms.vss-tfs-web.identity-image-route", { size: "0", id: team.id });
            const pickerItem = this.mapApiTeamToPickerItem(team, identiyImageUrl, "MoreTeams");
            teamItems.push(pickerItem);
        });

        return teamItems;
    }

    private mapApiTeamToPickerItem(team: WebApiTeam, identiyImageUrl: string, groupKey: string): ITeamPickerItem {
        return ({
            name: team.name,
            id: team.id,
            projectName: team.projectName,
            projectId: team.projectId,
            identityUrl: identiyImageUrl,
            groupKey: groupKey,
            description: team.description
        });
    }
}
